import React, { useEffect, useState } from 'react';
import {
  listPartners,
  createPartner,
  updatePartner,
  resetPartnerLogin,
  listReferrals,
  getPayoutReport,
  markReferralPaid,
  unmarkReferralPaid
} from './api';

const APP_URL = process.env.REACT_APP_PUBLIC_URL || 'https://app.mowstop.com';

const fmtMoney = (cents) => {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch (e) { return '—'; }
};

const EMPTY_FORM = {
  name: '', contact_email: '', contact_phone: '', code: '',
  payout_method: '', payout_handle: '', notes: ''
};

export default function Partners() {
  const [view, setView] = useState('partners'); // 'partners' | 'payouts'
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [report, setReport] = useState({ report: [], total_owed_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null); // { name, email, temp_password, portal_url }

  const refresh = async () => {
    try {
      const [p, r, rep] = await Promise.all([listPartners(), listReferrals(), getPayoutReport()]);
      setPartners(p.data || []);
      setReferrals(r.data || []);
      setReport(rep.data || { report: [], total_owed_cents: 0 });
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load referrals');
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await createPartner({
        name: form.name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || undefined,
        code: form.code || undefined,
        payout_method: form.payout_method || undefined,
        payout_handle: form.payout_handle || undefined,
        notes: form.notes || undefined
      });
      setForm(EMPTY_FORM);
      setShowAdd(false);
      setCredentials({
        name: data.partner?.name || form.name,
        email: form.contact_email,
        temp_password: data.temp_password,
        portal_url: data.portal_url
      });
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create partner');
    }
    setSaving(false);
  };

  const handleResetLogin = async (p) => {
    if (!window.confirm(`Reset ${p.name}'s portal login and issue a new temporary password?`)) return;
    try {
      const { data } = await resetPartnerLogin(p.id);
      setCredentials({
        name: p.name,
        email: p.contact_email,
        temp_password: data.temp_password,
        portal_url: data.portal_url
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset login');
    }
  };

  const togglePartnerStatus = async (p) => {
    const next = p.status === 'active' ? 'paused' : 'active';
    try {
      await updatePartner(p.id, { status: next });
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update partner');
    }
  };

  const handleMarkPaid = async (ref) => {
    const def = ref.payout_amount_cents != null ? (ref.payout_amount_cents / 100).toFixed(2) : '25.00';
    const input = window.prompt(`Payout amount in dollars for ${ref.businesses?.name || 'this referral'}:`, def);
    if (input == null) return;
    const dollars = Number(input);
    if (!Number.isFinite(dollars) || dollars < 0) { alert('Enter a valid dollar amount.'); return; }
    try {
      await markReferralPaid(ref.id, Math.round(dollars * 100));
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark paid');
    }
  };

  const handleUnmark = async (ref) => {
    if (!window.confirm('Undo "mark paid" for this referral?')) return;
    try {
      await unmarkReferralPaid(ref.id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not undo');
    }
  };

  // Mark every eligible (qualified + unpaid) referral for one partner as paid.
  const handlePayPartner = async (partnerId, partnerName) => {
    const items = referrals.filter((r) => r.partner_id === partnerId && r.qualified_at && !r.paid_at);
    if (items.length === 0) return;
    const total = items.reduce((s, r) => s + (r.payout_amount_cents || 0), 0);
    if (!window.confirm(`Mark all ${items.length} eligible payouts for ${partnerName} as paid (${fmtMoney(total)})?`)) return;
    try {
      for (const r of items) {
        await markReferralPaid(r.id, r.payout_amount_cents || 0); // eslint-disable-line no-await-in-loop
      }
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record payouts');
    }
  };

  const exportReportCsv = () => {
    const rows = [['Partner', 'Email', 'Method', 'Handle', 'Amount owed', 'Signups owed']];
    report.report.forEach((p) => {
      if (p.owed_cents > 0) {
        rows.push([
          p.name, p.contact_email || '', p.payout_method || '', p.payout_handle || '',
          (p.owed_cents / 100).toFixed(2), String(p.owed_items.length)
        ]);
      }
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mowstop-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

  const owedReferrals = referrals.filter((r) => r.qualified_at && !r.paid_at);
  const owedTotal = owedReferrals.reduce((sum, r) => sum + (r.payout_amount_cents || 0), 0);

  const CredentialsBanner = () => credentials && (
    <div style={styles.creds} role="status" aria-live="polite">
      <div style={styles.credsTitle}>✓ Login ready for {credentials.name} — share these once</div>
      <div style={styles.credRow}><span style={styles.credLabel}>Portal</span><code style={styles.code}>{credentials.portal_url}</code></div>
      <div style={styles.credRow}><span style={styles.credLabel}>Email</span><code style={styles.code}>{credentials.email}</code></div>
      {credentials.temp_password ? (
        <div style={styles.credRow}>
          <span style={styles.credLabel}>Temp password</span>
          <code style={styles.code}>{credentials.temp_password}</code>
          <button style={styles.smallBtn} onClick={() => copy(credentials.temp_password)}>Copy</button>
        </div>
      ) : (
        <div style={styles.credNote}>This email already had a login — they keep their existing password.</div>
      )}
      <div style={styles.credNote}>They'll be asked to set a new password the first time they sign in.</div>
      <button style={styles.dismissBtn} onClick={() => setCredentials(null)}>Done</button>
    </div>
  );

  return (
    <div>
      {error && <div style={styles.error} role="alert">{error}</div>}

      <div style={styles.statsRow}>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Partners</div>
          <div style={styles.statValue}>{partners.length}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Owed now</div>
          <div style={{ ...styles.statValue, color: '#b7791f' }}>{fmtMoney(owedTotal)}</div>
          <div style={styles.statNote}>{owedReferrals.length} eligible payouts</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Total qualified</div>
          <div style={{ ...styles.statValue, color: '#14653a' }}>{referrals.filter((r) => r.qualified_at).length}</div>
        </div>
      </div>

      <div style={styles.subTabs}>
        <button style={{ ...styles.subTab, ...(view === 'partners' ? styles.subTabActive : {}) }} onClick={() => setView('partners')}>Partners &amp; referrals</button>
        <button style={{ ...styles.subTab, ...(view === 'payouts' ? styles.subTabActive : {}) }} onClick={() => setView('payouts')}>Payout report</button>
      </div>

      <CredentialsBanner />

      {view === 'partners' && (
        <>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Partners</span>
            <button style={styles.addBtn} onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Cancel' : '+ Add partner'}</button>
          </div>

          <div style={styles.tierNote}>
            Payouts are automatic per qualified signup (paid 60 days): <b>$25</b> for signups 1–5,
            <b> $50</b> for 6–10, <b>$75</b> for 11+.
          </div>

          {showAdd && (
            <form onSubmit={handleCreate} style={styles.addCard}>
              <div style={styles.fieldRow}>
                <label style={styles.field}>
                  <span style={styles.label}>Shop name *</span>
                  <input style={styles.input} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bob's Mower Shop" />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Code (auto if blank)</span>
                  <input style={styles.input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="BOBSMOWER" />
                </label>
              </div>
              <div style={styles.fieldRow}>
                <label style={styles.field}>
                  <span style={styles.label}>Contact email * (their portal login)</span>
                  <input style={styles.input} type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Contact phone</span>
                  <input style={styles.input} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                </label>
              </div>
              <div style={styles.fieldRow}>
                <label style={styles.field}>
                  <span style={styles.label}>How you'll pay them</span>
                  <input style={styles.input} value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })} placeholder="check / venmo / zelle" />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Payout handle / mailing address</span>
                  <input style={styles.input} value={form.payout_handle} onChange={(e) => setForm({ ...form, payout_handle: e.target.value })} placeholder="@bobsmower or 123 Main St" />
                </label>
              </div>
              <label style={{ ...styles.field, marginBottom: 12 }}>
                <span style={styles.label}>Notes</span>
                <textarea style={{ ...styles.input, minHeight: 56 }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </label>
              <button type="submit" disabled={saving} style={styles.saveBtn}>{saving ? 'Creating…' : 'Create partner + login'}</button>
            </form>
          )}

          {loading ? (
            <div style={styles.loading} role="status" aria-live="polite">Loading…</div>
          ) : partners.length === 0 ? (
            <div style={styles.empty}>No partners yet. Add your first shop above.</div>
          ) : (
            <div style={styles.partnerList}>
              {partners.map((p) => {
                const url = `${APP_URL}/?ref=${p.code}`;
                return (
                  <div key={p.id} style={styles.partner}>
                    <div style={styles.partnerHead}>
                      <div>
                        <div style={styles.partnerName}>
                          {p.name}{' '}
                          <span style={{ ...styles.statusPill, ...(p.status === 'active' ? styles.statusActive : styles.statusPaused) }}>{p.status}</span>
                        </div>
                        <div style={styles.partnerMeta}>
                          Code <b>{p.code}</b>
                          {p.contact_email ? ` · ${p.contact_email}` : ''}
                          {p.payout_method ? ` · pays via ${p.payout_method}` : ''}
                        </div>
                      </div>
                      <div style={styles.partnerBtns}>
                        <button style={styles.smallBtn} onClick={() => handleResetLogin(p)}>Reset login</button>
                        <button style={styles.smallBtn} onClick={() => togglePartnerStatus(p)}>{p.status === 'active' ? 'Pause' : 'Activate'}</button>
                      </div>
                    </div>
                    <div style={styles.urlRow}>
                      <span style={styles.urlLabel}>Referral URL:</span>
                      <code style={styles.urlText}>{url}</code>
                      <button style={styles.smallBtn} onClick={() => copy(url)}>Copy</button>
                    </div>
                    <div style={styles.partnerCounts}>
                      Signed up <b>{p.counts.signed_up}</b> · Qualified <b style={{ color: '#14653a' }}>{p.counts.qualified}</b> · Paid <b>{p.counts.paid}</b>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ ...styles.sectionHeader, marginTop: 28 }}>
            <span style={styles.sectionTitle}>Referrals</span>
          </div>

          {referrals.length === 0 ? (
            <div style={styles.empty}>No referrals yet — they'll appear here when someone signs up via a code.</div>
          ) : (
            <div style={styles.refList}>
              {referrals.map((r) => {
                let statusPill;
                let action;
                if (r.paid_at) {
                  statusPill = <span style={{ ...styles.statusPill, ...styles.statusPaid }}>paid · {fmtDate(r.paid_at)}</span>;
                  action = <button style={styles.smallBtn} onClick={() => handleUnmark(r)}>Undo</button>;
                } else if (r.qualified_at) {
                  statusPill = <span style={{ ...styles.statusPill, ...styles.statusQualified }}>eligible · owe {fmtMoney(r.payout_amount_cents)}</span>;
                  action = <button style={styles.payBtn} onClick={() => handleMarkPaid(r)}>Mark paid</button>;
                } else {
                  statusPill = <span style={{ ...styles.statusPill, ...styles.statusPending }}>in trial / &lt;60d</span>;
                  action = null;
                }
                return (
                  <div key={r.id} style={styles.ref}>
                    <div style={{ flex: 1 }}>
                      <div style={styles.refName}>{r.businesses?.name || 'New signup'}</div>
                      <div style={styles.refMeta}>via <b>{r.referral_partners?.name || r.code_used}</b> · signed up {fmtDate(r.signed_up_at)}</div>
                    </div>
                    {statusPill}
                    {action}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {view === 'payouts' && (
        <>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>Owe now: {fmtMoney(report.total_owed_cents)}</span>
            <button style={styles.addBtn} onClick={exportReportCsv} disabled={report.total_owed_cents === 0}>Export CSV</button>
          </div>
          {report.report.filter((p) => p.owed_cents > 0).length === 0 ? (
            <div style={styles.empty}>Nothing owed right now. Partners show up here once a referral hits the 60-day paid mark.</div>
          ) : (
            <div style={styles.partnerList}>
              {report.report.filter((p) => p.owed_cents > 0).map((p) => (
                <div key={p.partner_id} style={styles.partner}>
                  <div style={styles.partnerHead}>
                    <div>
                      <div style={styles.partnerName}>{p.name} <span style={{ ...styles.statusPill, ...styles.statusQualified }}>{fmtMoney(p.owed_cents)} owed</span></div>
                      <div style={styles.partnerMeta}>
                        {p.payout_method ? `Pay via ${p.payout_method}` : 'No payout method set'}
                        {p.payout_handle ? ` → ${p.payout_handle}` : ''}
                        {p.contact_email ? ` · ${p.contact_email}` : ''}
                      </div>
                    </div>
                    <button style={styles.payBtn} onClick={() => handlePayPartner(p.partner_id, p.name)}>Mark all paid</button>
                  </div>
                  <div style={styles.partnerCounts}>
                    {p.owed_items.map((it, i) => (
                      <span key={i}>{i > 0 ? ' · ' : ''}{it.business_name} ({fmtMoney(it.amount_cents)})</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={styles.tierNote}>
            Lifetime paid out: <b>{fmtMoney(report.report.reduce((s, p) => s + (p.paid_cents || 0), 0))}</b>.
            Once you set up Stripe, this is where automated payouts will hook in.
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  loading: { textAlign: 'center', padding: 40, color: '#5f6b58' },
  empty: { background: '#fff', border: '1px dashed #e3e9df', borderRadius: 12, padding: '24px 18px', fontSize: 13.5, color: '#5f6b58', textAlign: 'center' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 18 },
  stat: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 12, padding: 16 },
  statLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5f6b58', marginBottom: 8 },
  statValue: { fontFamily: 'Manrope, sans-serif', fontSize: 24, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-.5px' },
  statNote: { fontSize: 11.5, color: '#5f6b58', marginTop: 4 },

  subTabs: { display: 'inline-flex', gap: 4, background: '#f3f4ef', borderRadius: 10, padding: 4, marginBottom: 16 },
  subTab: { padding: '8px 16px', borderRadius: 7, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: '#5f6b58', cursor: 'pointer', fontFamily: 'inherit' },
  subTabActive: { background: '#fff', color: '#14653a', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },

  creds: { background: '#ecf8ee', border: '1px solid #cfe9d4', borderRadius: 12, padding: 16, marginBottom: 18 },
  credsTitle: { fontWeight: 800, color: '#14653a', fontSize: 14, marginBottom: 10, fontFamily: 'Manrope, sans-serif' },
  credRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  credLabel: { fontSize: 12, fontWeight: 700, color: '#5f6b58', minWidth: 96 },
  code: { fontSize: 13, color: '#14653a', fontFamily: 'ui-monospace, SFMono-Regular, monospace', background: '#fff', padding: '3px 8px', borderRadius: 6, border: '1px solid #cfe9d4' },
  credNote: { fontSize: 12.5, color: '#5f6b58', marginTop: 4 },
  dismissBtn: { marginTop: 10, padding: '8px 16px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' },

  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 },
  sectionTitle: { fontFamily: 'Manrope, sans-serif', fontSize: 17, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px' },
  addBtn: { padding: '8px 14px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },
  tierNote: { background: '#f7f9f4', border: '1px solid #eef1eb', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: '#5f6b58', marginBottom: 14, lineHeight: 1.5 },

  addCard: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, padding: 18, marginBottom: 16 },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: 12, fontWeight: 600, color: '#5f6b58', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e3e9df', borderRadius: 8, fontSize: 14, color: '#1a1a1a', background: '#f7f9f4', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  saveBtn: { padding: '11px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },

  partnerList: { display: 'flex', flexDirection: 'column', gap: 10 },
  partner: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 12, padding: 14 },
  partnerHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  partnerName: { fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  partnerMeta: { fontSize: 12, color: '#5f6b58', marginTop: 3 },
  partnerBtns: { display: 'flex', gap: 6, flexShrink: 0 },
  partnerCounts: { fontSize: 12.5, color: '#5f6b58', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f2ec' },
  urlRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#f7f9f4', border: '1px solid #eef1eb', borderRadius: 8, flexWrap: 'wrap' },
  urlLabel: { fontSize: 11, fontWeight: 700, color: '#5f6b58' },
  urlText: { fontSize: 12, color: '#14653a', fontFamily: 'ui-monospace, SFMono-Regular, monospace', wordBreak: 'break-all', flex: 1 },

  refList: { display: 'flex', flexDirection: 'column', gap: 8 },
  ref: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e3e9df', borderRadius: 10, padding: 12 },
  refName: { fontSize: 14, fontWeight: 700, color: '#1a1a1a' },
  refMeta: { fontSize: 12, color: '#5f6b58', marginTop: 2 },

  statusPill: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 100, letterSpacing: '0.02em', textTransform: 'uppercase', flexShrink: 0 },
  statusActive: { background: '#ecf8ee', color: '#14653a' },
  statusPaused: { background: '#fdf3e0', color: '#b7791f' },
  statusPending: { background: '#f0f2ec', color: '#5f6b58' },
  statusQualified: { background: '#fdf3e0', color: '#b7791f' },
  statusPaid: { background: '#ecf8ee', color: '#14653a' },

  smallBtn: { padding: '6px 12px', border: '1px solid #e3e9df', background: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#5f6b58', cursor: 'pointer', fontFamily: 'inherit' },
  payBtn: { padding: '6px 12px', border: 'none', background: '#14653a', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }
};
